import { filterDOMProps } from '@react-aria/utils';
import type { DOMProps } from '@react-types/shared';
import type { ReactNode } from 'react';
import { Children, createContext, forwardRef, useMemo } from 'react';

import type { BaseStyleProps, UnsafeStyleProps } from '@infinex/ui-styles';
import { cn, styleVars, useStyleProps } from '@infinex/ui-styles';

import type { EmblemSize } from './constants';
import { EMBLEM_DIMENSIONS } from './constants';

export type EmblemGroupProps = {
  /** The children to render. */
  children: ReactNode;
  /**
   * The maximum number of emblems to render. If the number of children exceeds
   * this value a remainder count will be displayed beside the group
   * @default 3
   */
  max?: number;
  /**
   * The size of each emblem in the group.
   * @default medium
   */
  size?: EmblemSize;
} & DOMProps &
  BaseStyleProps &
  UnsafeStyleProps;

/**
 * An emblem group displays a number of tokens grouped together in a stack.
 *
 * [Taurus docs: `EmblemGroup`](https://ui-sb.app.infinex.dev/?path=/docs/components-emblemgroup--docs)
 */
export const EmblemGroup = forwardRef<HTMLDivElement, EmblemGroupProps>(
  function EmblemGroup(props, ref) {
    const { className, style } = useStyleProps(props);
    const {
      children,
      max: maxProp = 3,
      size = 'medium',
      ...otherProps
    } = props;

    const total = Children.count(children);
    const max = Math.max(maxProp, 1); // ensure at least one emblem is shown
    const remainder = useMemo(() => Math.max(0, total - max), [total, max]);

    return (
      <EmblemGroupContext.Provider value={useMemo(() => ({ size }), [size])}>
        <div
          {...filterDOMProps(otherProps)}
          ref={ref}
          className={cn('flex items-center gap-2', className)}
          style={style}
        >
          <div className="flex items-center">
            {Children.toArray(children)
              .slice(0, max)
              .map((child, idx) => {
                const isFirst = idx === 0;

                return (
                  <div
                    key={idx}
                    className={cn(
                      'relative size-(--size) rounded-full',
                      '[--radius:calc(var(--size)/2+var(--stroke)/2)]',
                      '[--overlap:calc(var(--radius)/-2+var(--stroke))]'
                    )}
                    style={styleVars({
                      marginInlineStart: !isFirst
                        ? 'round(up, var(--overlap), 1px)'
                        : undefined,
                      maskImage: !isFirst
                        ? 'radial-gradient(circle var(--radius) at calc(var(--radius) / -2) 50%, transparent 99%, #fff 100%)'
                        : undefined,
                      '--size': `${EMBLEM_DIMENSIONS[size].size}px`,
                      '--stroke': 'calc(var(--size) / 16)',
                    })}
                  >
                    {child}
                  </div>
                );
              })}
          </div>

          {remainder > 0 && <GroupRemainder value={remainder} size={size} />}
        </div>
      </EmblemGroupContext.Provider>
    );
  }
);

export const EmblemGroupContext = createContext<{
  size: keyof typeof EMBLEM_DIMENSIONS;
} | null>(null);

type GroupRemainderProps = {
  value: number;
} & Pick<EmblemGroupProps, 'size'>;
export function GroupRemainder(props: GroupRemainderProps) {
  const { size, value } = props;
  return (
    <div
      className={cn(
        'text-default font-numeric w-(--width) flex-none text-center',
        {
          'text-2xs font-medium': size === 'xsmall',
          'text-xs font-medium': size === 'small',
          'text-sm font-medium': size === 'medium',
          'text-base font-medium': size === 'large' || size === 'xlarge',
        }
      )}
    >
      +{value}
    </div>
  );
}
